/* eslint-disable react/require-default-props */
import React, { Suspense, useEffect, useMemo, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { useLoaderData } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { createStore, applyMiddleware, Store } from 'redux';
import thunk from 'redux-thunk';
import './scss/board.scss';
import { Provider } from 'react-redux';
import i18n from '../i18n';
import BoardContainerComp from './containers/board.container';
import BoardReducers from './reducers/board.reducer';
import BoardService from './services/board.service';
import * as CardAction from './actions/card.actions';
import * as ColumnAction from './actions/column.actions';
import { updateWsGraphData } from './utils/board';
import BoardConst from './utils/constants/board';
import EmptyBoardComp from './containers/board.empty';
import TopmenuContainer from './containers/topmenu.container';
import { withBoardLoadTime } from './utils/currentDate';

const boardSevice = new BoardService();

// Loader for SharedBoardPage
export async function sharedBoardLoader({ request }: any) {
  // Extract id from URL
  const url = new URL(request.url);
  const id = url.searchParams.get('id');
  if (!id) throw new Error('Missing board id');

  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load board data: ${response.status}`);
  }
  return response.json();
}

function useGlobalBoardViewModel(store: Store<any, any>) {
  const boardViewModel = useCallback(() => {
    const aiUpdate = async (actId: any) => {
      const aiData = await boardSevice.getAiDataForBoard({ id: actId });
      if (store && typeof store.dispatch != undefined) {
        if (aiData && aiData.data) {
          store.dispatch(CardAction.updateBasedOnAIPopup(aiData.data.data));
          updateWsGraphData(aiData.data.data.tagId);
        }
      }
    }
    const updateBoardTimeline = (obj: any) => {
      if (Object.keys(obj).length) {
        store.dispatch(ColumnAction.updateBoardTimeline(obj));
      }
    }

    const addAiOnCopy = (data: any, currentAiId: string) => {
      if (store && typeof store.dispatch != 'undefined') {
        store.dispatch(CardAction.copyAICard({ data, currentAiId }));
        updateWsGraphData(data.tagId);
      }
    }

    const removeAiFromBoard = (aiId: string) => {
      if (store && typeof store.dispatch != 'undefined') {
        store.dispatch(CardAction.deleteCard('', aiId));
      }
    }

    return {
      addAiOnCopy, aiUpdate, updateBoardTimeline, removeAiFromBoard
    }
  }, [store]);

  useEffect(() => {
    // Expose the boardViewModel via the global window object
    window.boardViewModel = boardViewModel;
    return () => {
      // Clean up the global reference when the component unmounts
      window.boardViewModel = undefined;
    }
  }, [boardViewModel]);
}

function SharedBoard({ data, className }: { data: any, className?: string }) {
  // Memoize store to avoid recreating on every render
  const store = useMemo(() => createStore(BoardReducers, withBoardLoadTime(data), applyMiddleware(thunk)), [data]);
  useGlobalBoardViewModel(store);

  const { boardDetails, boardView } = data;
  const { boardEmpty = false } = boardDetails;

  useEffect(() => {
    // Call the global resize function after render
    const id = setTimeout(() => {
      window.sharedBoardViewResize?.();
    }, 100);
    return () => clearTimeout(id);
  }, [data]);

  return (
    <Provider store={store}>
      <Suspense fallback="loading...">
        <I18nextProvider i18n={i18n}>
          <div className={`wsBoardWrapper ${className}`}>
            <div id="wsBoardTopMenuWrapper">
              {!boardEmpty && boardView === BoardConst.BOARD_VIEW_SHARED && <TopmenuContainer />}
            </div>
            <div id="wsBoardOuterTableWrapper">
              {boardEmpty && boardView === BoardConst.BOARD_VIEW_SHARED
                ? <EmptyBoardComp />
                : <BoardContainerComp />}
            </div>
          </div>
          <div id="board_add_board_module" className="hidden" />
          <div className="sharedBoardStyleAppend" />
        </I18nextProvider>
      </Suspense>
    </Provider>
  );
}

export function SharedBoardPage() {
  const data = useLoaderData() as any;
  return (
    <SharedBoard data={data} className="sharedBoardWrap" />
  );
}

window.initSharedBoardComp = (data: any) => {
  const el = document.getElementById('wsBoardRoot');
  const root = createRoot(el);
  root.render(
    <SharedBoard data={data} className="wsAiBoard" />
  );
}

function KebabMenu({ data }: { data: any }) {
  // Memoize store to avoid recreating on every render
  const store = useMemo(() => createStore(BoardReducers, data, applyMiddleware(thunk)), [data]);


  return (
    <Provider store={store}>
      <Suspense fallback="...">
        <I18nextProvider i18n={i18n}>
          <TopmenuContainer />
        </I18nextProvider>
      </Suspense>
    </Provider>
  );
}

window.initSharedBoardKebabMenu = (data: any) => {
  const menuEl = document.getElementById('wsBoardTopMenuWrapper');
  const menuRoot = createRoot(menuEl);

  menuRoot.render(<KebabMenu data={data} />);
};
